/* The picker: one row of buttons per rung of the tree, from the stakes down to the answer an
   opponent gave. Options the collection holds no chart for are greyed out; in edit mode they
   are the way in to the creator. */

import {
  BRANCH_HELP,
  actionFault,
  actionHelp,
  actionLabel,
  branchLabel,
  branchesFor,
  chartedAt,
  chartedBranches,
  chartedProfiles,
  chartedSeatsAt,
  seatFault,
  seatHelp,
  seatUnusable,
  seatsFor,
  type Dim,
  type Spot,
  seatName,
} from "@preflop/core";
import { el, fill } from "../../ui/dom";
import { group } from "../../ui/tree";
import { paintChart } from "./chart";
import { openCreator, openVariation } from "./creator";
import { dom } from "./layout";
import { parkNotation } from "./notation-panel";
import { BASIC, BASIC_HELP, profileColor } from "../../ui/profiles";
import { randomizerGroup } from "./randomizer";
import {
  CATALOG,
  addToCatalog,
  confirmDiscard,
  page,
  profiles,
  render,
  type Ladder,
} from "./state";

const LABELS = { stakes: "Stakes", gameType: "Game Type", format: "Format", stack: "Stack Size" };
const DIM_TIP: Readonly<Record<string, (v: string) => string>> = {
  stakes: (v) => `Charts written for ${v}`,
  gameType: (v) => `Charts for ${v.toLowerCase()}`,
  format: (v) => `Charts for a ${v} table`,
};

export function renderControls(spot: Spot): void {
  const p = page();
  const host = dom.picker;
  parkNotation(); // clearing the host would take it along
  fill(host);
  host.hidden = !!p.creator; // the creator takes this column over
  if (host.hidden) return;

  const editing = p.editMode;
  const tree = p.tree;
  const gameType = tree.gameType ?? spot.gameType;
  const hero = tree.hero ?? spot.hero;
  const action = tree.action ?? spot.action;

  const rows: HTMLElement[] = [];
  const mk = (cls = ""): HTMLElement => {
    const r = el("div", { class: "row" + (cls ? " " + cls : "") });
    rows.push(r);
    return r;
  };
  const set = (k: Dim, v: string): void => {
    if (!confirmDiscard()) return;
    tree[k] = v;
    p.isolate = null;
    render();
  };

  // "+" adds a rung and stops there: the size joins the ladder greyed out, and the chart
  // behind it is written by clicking it, not by typing it.
  const sizeHelp = (key: Ladder, v: string): string =>
    key === "stack" ? `Everyone sits with ${v}` : `The first raise is ${v}`;

  const ladderGroup = (label: string, key: Ladder): HTMLElement => {
    const g = group(
      label,
      CATALOG[key],
      chartedAt(p.spots, tree, key),
      tree[key],
      (v) => set(key, v),
      {
        dim: key,
        editing,
        titleOf: (v) => sizeHelp(key, v),
        // A dimension with no chart behind it is not a dead end in edit mode: it is the
        // starting point of a new one.
        onCreate: (v) => openCreator(key === "stack" ? { stack: v } : { openSize: v }),
        onAdd: () => {
          p.adding = key;
          renderControls(spot);
        },
      },
    );
    if (p.adding !== key) return g;
    const inp = el("input", { type: "text", placeholder: key === "stack" ? "35bb" : "3.25x" });
    // Added is a catalog change, not a navigation: a full render only to pick the new button
    // up in the list, none at all when nothing was typed.
    const done = (added: boolean): void => {
      p.adding = null;
      if (added) render();
      else renderControls(spot);
    };
    inp.addEventListener("keydown", (e) => {
      if (e.key === "Enter") done(addToCatalog(key, inp.value));
      else if (e.key === "Escape") done(false);
    });
    inp.addEventListener("blur", () => {
      if (p.adding === key) done(addToCatalog(key, inp.value));
    });
    g.append(el("div", { class: "meta" }, el("label", {}, `New ${label.toLowerCase()}`, inp)));
    setTimeout(() => inp.focus(), 0);
    return g;
  };

  // A dimension with a single value is context, not a choice: it moves to the caption. The
  // selector reappears on its own the day a second value shows up.
  const TOP = ["stakes", "gameType", "format", "stack"] as const;
  const held = Object.fromEntries(TOP.map((d) => [d, chartedAt(p.spots, tree, d)])) as Record<
    (typeof TOP)[number],
    string[]
  >;
  // Stack is editable, so in edit mode it stays a selector even when the collection holds a
  // single value: that is where a second one is added.
  const isChoice = (d: (typeof TOP)[number]): boolean =>
    held[d].length > 1 || (editing && d === "stack");
  const fixed = TOP.filter((d) => !isChoice(d));
  const choices = TOP.filter(isChoice);

  if (fixed.length)
    host.append(el("p", { class: "caption" }, fixed.map((d) => held[d][0] ?? "").join(" · ")));

  // The randomizer rides the first row, hard right, and only while reading: editing, the
  // number names nothing, because the grid shows the mix the brush works on rather than the
  // action one roll picks out of it. With every dimension up here fixed and no randomizer to
  // carry, the row is not drawn at all — an empty one is still a panel.
  const dice = editing ? null : randomizerGroup(() => paintChart());
  if (choices.length || dice) {
    const firstRow = mk("split");
    for (const d of choices)
      firstRow.append(
        d === "stack"
          ? ladderGroup(LABELS[d], "stack")
          : group(LABELS[d], CATALOG[d], held[d], tree[d], (v) => set(d, v), {
              dim: d,
              editing,
              ...(DIM_TIP[d] ? { titleOf: DIM_TIP[d] } : {}),
            }),
      );
    if (dice) firstRow.append(dice);
  }

  mk().append(ladderGroup("Open Raise", "openSize"));
  // The seat pool is the table, not the catalog: heads-up has no CO to offer.
  const pool = seatsFor(gameType);
  mk().append(
    group("Hero Seat", pool, chartedAt(p.spots, tree, "hero"), tree.hero, (v) => set("hero", v), {
      dim: "hero",
      editing,
      labelOf: seatName,
      titleOf: seatHelp,
      onCreate: (v) => openCreator({ hero: v }),
      faultOf: (v) => seatUnusable(gameType, v),
    }),
  );
  // Read against the seat above, so a spot LJ can never be in is not offered as a gap to
  // fill: no table ever deals it.
  mk().append(
    group(
      "Preflop Action",
      CATALOG.action,
      chartedAt(p.spots, tree, "action"),
      tree.action,
      (v) => set("action", v),
      {
        dim: "action",
        editing,
        labelOf: actionLabel,
        titleOf: actionHelp,
        onCreate: (v) => openCreator({ action: v }),
        faultOf: (v) => actionFault(gameType, hero, v),
      },
    ),
  );

  spot.seats.forEach((_, i) => {
    const label = spot.seatLabels[i] ?? "Opponent Seat";
    const here = tree.seats.slice(0, spot.seats.length);
    mk().append(
      group(
        label,
        pool,
        chartedSeatsAt(p.spots, tree, i),
        tree.seats[i],
        (v) => {
          if (!confirmDiscard()) return;
          tree.seats[i] = v;
          tree.seats.length = i + 1;
          p.isolate = null;
          render();
        },
        {
          dim: `seat${i}`,
          editing,
          labelOf: seatName,
          titleOf: seatHelp,
          onCreate: (v) => {
            const seats = [...here];
            seats[i] = v;
            openCreator({ seats });
          },
          faultOf: (v) => seatFault(gameType, hero, action, here, i, v),
        },
      ),
    );
  });

  // Only where an opponent still had a decision between the last raise and hero's. There the
  // row is a rung like any other: every answer the table allows is offered, and the ones with
  // no chart behind them are grey rather than absent — a squeeze the opener folded to is a
  // spot whether or not this collection charted it.
  const answers = branchesFor(spot.action);
  if (answers) {
    const charted = chartedBranches(p.spots, tree, spot.seats.length).filter(
      (b): b is string => !!b,
    );
    mk().append(
      group(
        branchLabel(spot.seats),
        answers,
        charted,
        tree.branch,
        (v) => {
          if (!confirmDiscard()) return;
          tree.branch = v;
          p.isolate = null;
          render();
        },
        {
          dim: "branch",
          editing,
          titleOf: (v) => BRANCH_HELP[v] ?? v,
          onCreate: (v) => openCreator({ branch: v }),
        },
      ),
    );
  }

  /* The last rung, always drawn: which version of this spot is on screen. `Basic Strategy`
     is the chart itself and comes first; then the collection's profiles, each wearing a dot
     of its colour, with the author's own note on hover. A grey one in edit mode opens the
     creator on a copy of the spot's own chart.

     The row is called Variations rather than Opponent because that is what it holds: the
     chart and the variations written against a kind of opponent. The profile is what each
     button names; the row is what they are. */
  const list = profiles();
  const charted = chartedProfiles(p.spots, tree, spot.seats.length);
  mk().append(
    group(
      "Variations",
      [BASIC, ...list.map((o) => o.id)],
      charted.map((v) => v ?? BASIC),
      tree.profile ?? BASIC,
      (v) => {
        if (!confirmDiscard()) return;
        tree.profile = v === BASIC ? null : v;
        p.isolate = null;
        render();
      },
      {
        dim: "profile",
        editing,
        keyOf: (v) => v,
        labelOf: (v) => (v === BASIC ? v : (list.find((o) => o.id === v)?.name ?? v)),
        dotOf: (v) => (v === BASIC ? "" : profileColor(list, v)),
        // The note the owner wrote on the collections page is read from here and nowhere
        // else, so every reader of the collection sees the same words on hover.
        titleOf: (v) => (v === BASIC ? BASIC_HELP : (list.find((o) => o.id === v)?.note ?? "")),
        onCreate: (v) => openVariation(v === BASIC ? null : v),
      },
    ),
  );

  host.append(...rows);
}
